import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  customerSearchColumns,
  dotThanhToanSearchColumns,
  hoaDonSearchColumns,
  hopDongSearchColumns,
  khachHangSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  mauSoHoaDonSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  taiKhoanNganHangSearchColumns,
  thueSearchColumns,
  tinhChatThueSearchColumns,
  vatTuSearchColumns,
  vuViecSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  type ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KhachHang,
  KheUoc,
  type LenhSanXuat,
  MauSoHoaDon,
  Phi,
  type TaiKhoanNganHang,
  type Tax,
  type TinhChatThue,
  type PhieuChiInvoiceLookup,
  VatTu,
  VuViec
} from '@/types/schemas';
import { CellFieldV2 as CellField, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import type { PhieuChiCellValue, PhieuChiDetailRow } from '../../../types';
import type { InputTableCellParams } from '@/components/custom/erp';
import { FormMode } from '@/types/form';

const getBankAccountDisplay = (value: PhieuChiDetailRow['tknh2_data']) => {
  if (!value) return '';
  if ('tknh' in value) return value.tknh;
  if ('code' in value) return value.code || '';
  return '';
};

export const getDetailTableColumns = (
  ma_ngv: string,
  dien_giai: string | null,
  displayTax: boolean,
  onCellValueChange: (rowUuid: string, field: string, newValue: PhieuChiCellValue) => void,
  getCustomerBalance?: (customerUuid: string, accountUuid?: string) => string,
  formMode?: FormMode
) => [
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 250,
    renderCell: (params: InputTableCellParams<PhieuChiDetailRow>) => (
      <CellField
        name='dien_giai'
        type='text'
        value={params.row.dien_giai || dien_giai || ''}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'dien_giai', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  {
    field: 'ma_kh',
    headerName: 'Mã đối tượng',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<KhachHang>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
        searchColumns={khachHangSearchColumns}
        columnDisplay='customer_code'
        dialogTitle='Danh mục đối tượng'
        value={params.row.ma_kh_data?.customer_code || params.row.ma_kh_data?.customer_number}
        disabled={formMode === 'view'}
        onRowSelection={(row: KhachHang) => onCellValueChange(params.row.uuid, 'ma_kh_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_kh',
      resultField: 'ma_kh_data',
      detailEndpoint: QUERY_KEYS.NHA_CUNG_CAP
    }
  },
  {
    field: 'ten_kh',
    headerName: 'Tên đối tượng',
    width: 230,
    renderCell: (params: InputTableCellParams) => (
      <CellField name='ten_kh' type='text' value={params.row.ma_kh_data?.customer_name} />
    ),
    nonWritable: true
  },

  ...(ma_ngv === '4'
    ? [
        {
          field: 'tknh2',
          headerName: 'Ngân hàng',
          width: 170,
          renderCell: (params: InputTableCellParams<PhieuChiDetailRow>) => (
            <SearchField<TaiKhoanNganHang>
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN_NGAN_HANG}/`}
              searchColumns={taiKhoanNganHangSearchColumns}
              columnDisplay='tknh'
              dialogTitle='Danh mục tài khoản'
              value={getBankAccountDisplay(params.row.tknh2_data)}
              disabled={formMode === 'view'}
              onRowSelection={(row: TaiKhoanNganHang) => onCellValueChange(params.row.uuid, 'tknh2_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'tknh2',
            resultField: 'tknh2_data',
            detailEndpoint: QUERY_KEYS.TAI_KHOAN_NGAN_HANG
          }
        }
      ]
    : [
        {
          field: 'du_cn',
          headerName: 'Dư công nợ',
          width: 150,
          renderCell: (params: InputTableCellParams) => {
            const customerUuid = params.row.ma_kh_data?.uuid;
            const balance =
              customerUuid && getCustomerBalance
                ? getCustomerBalance(customerUuid, params.row.tk_no_data?.uuid)
                : params.row.du_cn || '0.00';

            return <CellField name='du_cn' type='number' value={balance} />;
          },
          nonWritable: true
        }
      ]),
  ...(ma_ngv !== '1'
    ? [
        {
          field: 'tk_no',
          headerName: 'Tài khoản nợ',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<AccountModel>
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              columnDisplay='code'
              dialogTitle='Danh mục tài khoản'
              value={params.row.tk_no_data?.code}
              disabled={formMode === 'view'}
              onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_no_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'tk_no',
            resultField: 'tk_no_data',
            detailEndpoint: QUERY_KEYS.TAI_KHOAN
          }
        }
      ]
    : []),
  ...(ma_ngv === '1'
    ? [
        {
          field: 'id_hd',
          headerName: 'Hoá đơn',
          width: 180,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<PhieuChiInvoiceLookup>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.HOA_DON_THEO_KHACH_HANG}/?ma_kh=${params.row.ma_kh_data?.uuid}&document_type=PHIEU_CHI`}
              searchColumns={hoaDonSearchColumns}
              columnDisplay='so_ct'
              dialogTitle='Hoá đơn'
              disabled={!params.row.ma_kh_data?.uuid || formMode === 'view'}
              value={params.row.id_hd_data?.so_ct || ''}
              onRowSelection={(row: PhieuChiDetailRow['id_hd_data']) =>
                onCellValueChange(params.row.uuid, 'id_hd_data', row)
              }
            />
          )
        },
        {
          field: 'so_ct0_hd',
          headerName: 'Số hoá đơn',
          width: 120,
          renderCell: (params: InputTableCellParams<PhieuChiDetailRow>) => (
            <CellField name='so_ct0_hd' type='text' value={params.row.id_hd_data?.so_hd} />
          ),
          nonWritable: true
        },
        {
          field: 'ngay_ct_hd',
          headerName: 'Ngày hoá đơn',
          width: 160,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ngay_ct_hd'
              type='date'
              value={params.row.id_hd_data?.hoa_don_data?.ngay_ct || params.row.id_hd_data?.ngay_ct}
            />
          ),
          nonWritable: true
        },
        {
          field: 'tk_no',
          headerName: 'Tài khoản nợ',
          width: 100,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='tk_no' value={params.row.id_hd_data?.tk_data?.tk || params.row.tk_no_data?.code} />
          ),
          nonWritable: true
        },
        {
          field: 'ma_ngt_hd',
          headerName: 'Ngoại tệ',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='ma_ngt_hd' type='text' value={params.row.id_hd_data?.ngoai_te} />
          ),
          nonWritable: true
        },
        {
          field: 'ty_gia_hd',
          headerName: 'Tỷ giá hđ',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='ty_gia_hd' type='number' value={params.row.ty_gia_hd || 1} />
          ),
          nonWritable: true
        },
        {
          field: 'tien_tren_hd',
          headerName: 'Tiền trên hóa đơn',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='tien_tren_hd' type='number' value={params.row.id_hd_data?.tien_tren_hd || 0} />
          ),
          nonWritable: true
        },
        {
          field: 'da_pb',
          headerName: 'Đã phân bổ',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='da_pb'
              type='number'
              value={params.row.id_hd_data?.da_thanh_toan ?? params.row.da_pb ?? 0}
            />
          ),
          nonWritable: true
        },
        {
          field: 'con_lai',
          headerName: 'Còn lại',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='tien_con_phai_tt' type='number' value={params.row.id_hd_data?.tien_con_phai_tt} />
          ),
          nonWritable: true
        }
      ]
    : []),
  {
    field: 'tien_nt',
    headerName: 'Tiền VND',
    width: 150,
    renderCell: (params: InputTableCellParams) => (
      <CellField
        name='tien_hd_nt'
        type='number'
        value={params.row.tien_nt}
        onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
        disabled={formMode === 'view'}
      />
    )
  },
  ...(ma_ngv === '2' || ma_ngv === '3'
    ? [
        {
          field: 'ma_loai_hd',
          headerName: 'Loại hoá đơn',
          width: 220,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ma_loai_hd'
              type='select'
              value={params.row.ma_loai_hd}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_loai_hd', newValue)}
              disabled={formMode === 'view'}
              options={[
                {
                  value: '0',
                  label: '0. Không có hóa đơn'
                },
                {
                  value: '1',
                  label: '1. Hóa đơn GTGT'
                },
                {
                  value: '2',
                  label: '2. Hóa đơn bán hàng thông thường'
                }
              ]}
            />
          )
        }
      ]
    : []),
  ...(displayTax && (ma_ngv === '2' || ma_ngv === '3')
    ? [
        {
          field: 'ma_thue',
          headerName: 'Mã thuế',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<Tax>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.THUE}/`}
              searchColumns={thueSearchColumns}
              columnDisplay='ma_thue'
              dialogTitle='Danh mục thuế'
              value={params.row.ma_thue_data?.ma_thue || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: Tax) => onCellValueChange(params.row.uuid, 'ma_thue_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_thue',
            resultField: 'ma_thue_data',
            detailEndpoint: QUERY_KEYS.THUE
          }
        },
        {
          field: 'ten_thue',
          headerName: 'Tên thuế',
          width: 150,
          renderCell: (params: InputTableCellParams) => (
            <CellField name='ten_thue' type='text' value={params.row.ma_thue_data?.ten_thue || ''} />
          ),
          nonWritable: true
        },
        {
          field: 'tk_thue',
          headerName: 'Tk thuế',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<AccountModel>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              searchColumns={accountSearchColumns}
              columnDisplay='code'
              dialogTitle='Danh mục tài khoản'
              value={params.row.tk_thue_data?.code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_thue_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'tk_thue',
            resultField: 'tk_thue_data',
            detailEndpoint: QUERY_KEYS.TAI_KHOAN
          }
        },
        {
          field: 'so_ct0',
          headerName: 'Số hoá đơn',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='so_ct0'
              type='text'
              value={params.row.so_ct0 || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct0', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'so_ct2',
          headerName: 'Ký hiệu',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='so_ct2'
              type='text'
              value={params.row.so_ct2 || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_ct2', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ngay_ct0',
          headerName: 'Ngày hoá đơn',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ngay_ct0'
              type='date'
              value={params.row.ngay_ct0 || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ngay_ct0', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        {
          field: 'ma_mau_ct',
          headerName: 'Mẫu HĐ',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<MauSoHoaDon>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.MAU_SO_HOA_DON}/`}
              searchColumns={mauSoHoaDonSearchColumns}
              columnDisplay='ma_mau_so'
              dialogTitle='Danh mục mẫu chứng từ'
              value={params.row.ma_mau_ct_data?.ma_mau_so || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: MauSoHoaDon) => onCellValueChange(params.row.uuid, 'ma_mau_ct_data', row)}
            />
          )
        },
        // ma_mau_bc - Mã mẫu báo cáo
        {
          field: 'ma_mau_bc',
          headerName: 'Mẫu báo cáo',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ma_mau_bc'
              type='select'
              options={[
                { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
                { value: '4', label: '4. Hàng hóa, dịch vụ mua vào không có hóa đơn' },
                { value: '5', label: '5. Hóa đơn bán hàng thông thường' }
              ]}
              value={params.row.ma_mau_bc || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_mau_bc', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ma_tc_thue',
          headerName: 'Mã tính chất',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<TinhChatThue>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.TINH_CHAT_THUE}/`}
              searchColumns={tinhChatThueSearchColumns}
              columnDisplay='ma_tc_thue'
              dialogTitle='Danh mục tính chất thuế'
              value={params.row.ma_tc_thue_data?.customer_code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: TinhChatThue) => onCellValueChange(params.row.uuid, 'ma_tc_thue_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_tc_thue',
            resultField: 'ma_tc_thue_data',
            detailEndpoint: QUERY_KEYS.TINH_CHAT_THUE
          }
        },
        {
          field: 'ma_kh_thue',
          headerName: 'Mã ncc',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<KhachHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={customerSearchColumns}
              columnDisplay='customer_code'
              dialogTitle='Danh mục đối tượng'
              value={params.row.ma_kh_thue_data?.customer_code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: KhachHang) => onCellValueChange(params.row.uuid, 'ma_kh_thue_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_kh_thue',
            resultField: 'ma_kh_thue_data',
            detailEndpoint: QUERY_KEYS.KHACH_HANG
          }
        },
        // ten_kh_thue - Tên khách hàng thuế
        {
          field: 'ten_kh_thue',
          headerName: 'Tên nhà cung cấp',
          width: 150,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ten_kh_thue'
              type='text'
              value={params.row.ma_kh_thue_data?.customer_name || params.row.ten_kh_thue || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_kh_thue', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        // dia_chi - Địa chỉ
        {
          field: 'dia_chi',
          headerName: 'Địa chỉ',
          width: 200,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='dia_chi'
              type='text'
              value={params.row.dia_chi || params.row.ma_kh_thue_data?.address || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'dia_chi', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        // ma_so_thue - Mã số thuế
        {
          field: 'ma_so_thue',
          headerName: 'Mã số thuế',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ma_so_thue'
              type='text'
              value={params.row.ma_so_thue || params.row.ma_kh_thue_data?.tax_code || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ma_so_thue', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        // ten_vt_thue - Tên vật tư thuế
        {
          field: 'ten_vt_thue',
          headerName: 'Tên hàng hóa - dịch vụ',
          width: 150,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='ten_vt_thue'
              type='text'
              value={params.row.ten_vt_thue || ''}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_vt_thue', newValue)}
              disabled={formMode === 'view'}
            />
          ),
          nonWritable: true
        },
        // thue_nt - Thuế ngoại tệ
        {
          field: 'thue_nt',
          headerName: 'Thuế NT',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='thue_nt'
              type='number'
              value={params.row.thue_nt || 0.0}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'thue',
          headerName: 'Thuế VND',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <CellField
              name='thue'
              type='number'
              value={params.row.thue_nt || 0.0}
              onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue', newValue)}
              disabled={formMode === 'view'}
            />
          )
        },
        {
          field: 'ma_kh9',
          headerName: 'Cục thuế',
          width: 120,
          renderCell: (params: InputTableCellParams) => (
            <SearchField<KhachHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}/`}
              searchColumns={customerSearchColumns}
              columnDisplay='customer_code'
              dialogTitle='Danh mục đối tượng'
              value={params.row.ma_kh9_data?.customer_code || ''}
              disabled={formMode === 'view'}
              onRowSelection={(row: KhachHang) => onCellValueChange(params.row.uuid, 'ma_kh9_data', row)}
            />
          ),
          pasteConfig: {
            lookupKey: 'ma_kh9',
            resultField: 'ma_kh9_data',
            detailEndpoint: QUERY_KEYS.KHACH_HANG
          }
        }
      ]
    : []),
  {
    field: 'so_ct_dn',
    headerName: 'Số đề nghị',
    width: 120,
    nonWritable: true
  },
  {
    field: 'line_dn',
    headerName: 'Dòng ĐN',
    width: 100,
    nonWritable: true
  },
  {
    field: 'ma_bp',
    headerName: 'Bộ phận',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<BoPhan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
        searchColumns={boPhanSearchColumns}
        columnDisplay='ma_bp'
        dialogTitle='Danh mục bộ phận'
        value={params.row.ma_bp_data?.ma_bp || params.row.ma_bp_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: BoPhan) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_bp',
      resultField: 'ma_bp_data',
      detailEndpoint: QUERY_KEYS.BO_PHAN
    }
  },
  {
    field: 'ma_vv',
    headerName: 'Vụ việc',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<VuViec>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
        searchColumns={vuViecSearchColumns}
        columnDisplay='ma_vu_viec'
        dialogTitle='Danh mục vụ việc'
        value={params.row.ma_vv_data?.ma_vu_viec || params.row.ma_vv_data?.code || params.row.ma_vv_data?.ma_vv}
        disabled={formMode === 'view'}
        onRowSelection={(row: VuViec) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_vv',
      resultField: 'ma_vv_data',
      detailEndpoint: QUERY_KEYS.VU_VIEC
    }
  },
  {
    field: 'ma_hd',
    headerName: 'Hợp đồng',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<HopDong>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
        searchColumns={hopDongSearchColumns}
        columnDisplay='ma_hd'
        dialogTitle='Danh mục hợp đồng'
        value={params.row.ma_hd_data?.ma_hd || params.row.ma_hd_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: HopDong) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_hd',
      resultField: 'ma_hd_data',
      detailEndpoint: QUERY_KEYS.HOP_DONG
    }
  },
  {
    field: 'ma_dtt',
    headerName: 'Đợt thanh toán',
    width: 140,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<DotThanhToan>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
        searchColumns={dotThanhToanSearchColumns}
        columnDisplay='ma_dtt'
        dialogTitle='Danh mục đợt thanh toán'
        value={params.row.ma_dtt_data?.ma_dtt || params.row.ma_dtt_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: DotThanhToan) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_dtt',
      resultField: 'ma_dtt_data',
      detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
    }
  },
  {
    field: 'ma_ku',
    headerName: 'Khế ước',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<KheUoc>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
        searchColumns={kheUocSearchColumns}
        columnDisplay='ma_ku'
        dialogTitle='Danh mục khế ước'
        value={params.row.ma_ku_data?.ma_ku || params.row.ma_ku_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: KheUoc) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_ku',
      resultField: 'ma_ku_data',
      detailEndpoint: QUERY_KEYS.KHE_UOC
    }
  },
  {
    field: 'ma_phi',
    headerName: 'Phí',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<Phi>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.PHI}/`}
        searchColumns={phiSearchColumns}
        columnDisplay='ma_phi'
        dialogTitle='Danh mục phí'
        value={params.row.ma_phi_data?.ma_phi || params.row.ma_phi_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: Phi) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_phi',
      resultField: 'ma_phi_data',
      detailEndpoint: QUERY_KEYS.PHI
    }
  },
  {
    field: 'ma_sp',
    headerName: 'Sản phẩm',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<VatTu>
        type='text'
        searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
        searchColumns={vatTuSearchColumns}
        columnDisplay='ma_vt'
        dialogTitle='Danh mục sản phẩm'
        value={params.row.ma_sp_data?.ma_vt || params.row.ma_sp_data?.code || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: VatTu) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_sp',
      resultField: 'ma_sp_data',
      detailEndpoint: QUERY_KEYS.VAT_TU
    }
  },
  {
    field: 'ma_lsx',
    headerName: 'Mã lệnh sản xuất',
    width: 140,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<LenhSanXuat>
        type='text'
        searchEndpoint={'/'}
        searchColumns={lenhSanXuatSearchColumns}
        columnDisplay='so_ct'
        dialogTitle='Danh mục lệnh sản xuất'
        value={params.row.ma_lsx_data?.so_ct || ''}
        disabled={formMode === 'view'}
        onRowSelection={(row: LenhSanXuat) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_lsx',
      resultField: 'ma_lsx_data',
      detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
    }
  },
  {
    field: 'ma_cp0',
    headerName: 'C/p không h/lệ',
    width: 120,
    renderCell: (params: InputTableCellParams) => (
      <SearchField<ChiPhiKhongHopLeData>
        searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
        searchColumns={chiPhiKhongHopLeSearchColumns}
        dialogTitle='Danh mục chi phí không hợp lệ'
        columnDisplay='ma_cp_khl'
        value={params.row.ma_cp0_data?.ma_cpkhl || '' || params.row.ma_cp0_data?.ma_cp}
        disabled={formMode === 'view'}
        onRowSelection={(row: ChiPhiKhongHopLeData) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
      />
    ),
    pasteConfig: {
      lookupKey: 'ma_cp0',
      resultField: 'ma_cp0_data',
      detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
    }
  }
];
